/**
 * useModalClose
 * ─────────────
 *
 * Reusable hook that hardens any "fixed overlay" modal in the app with
 * three escape hatches matching iOS HIG / WCAG 2.1 standards:
 *
 *   1. ESC key (window keydown)
 *   2. Backdrop click (only when the click target IS the overlay itself)
 *   3. Whatever close button you wired (caller's responsibility)
 *
 * Why a hook instead of a wrapper component:
 *   - Doesn't force a particular markup. Each modal already has its
 *     unique design language; the hook just adds behavior.
 *   - Caller decides whether the modal is "dismissible" (some are not —
 *     e.g., a destructive-confirm modal should NOT close on backdrop).
 *
 * Usage:
 *   const { backdropProps, cardProps } = useModalClose({ open, onClose });
 *   ...
 *   <div {...backdropProps} className="fixed inset-0 ...">
 *     <div {...cardProps} className="rounded-3xl ...">
 *       <button onClick={onClose}>X</button>
 *     </div>
 *   </div>
 *
 * Options:
 *   - `dismissOnEscape`     (default true)  — listen for ESC and call onClose
 *   - `dismissOnBackdrop`   (default true)  — backdrop-click triggers onClose
 */
import { useEffect, useMemo } from 'react';

export function useModalClose({ open, onClose, dismissOnEscape = true, dismissOnBackdrop = true }) {
  // ESC listener — attached only while the modal is open.
  useEffect(() => {
    if (!open || !dismissOnEscape || typeof onClose !== 'function') return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(e); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismissOnEscape, onClose]);

  const backdropProps = useMemo(() => ({
    onClick: (e) => {
      if (!dismissOnBackdrop || typeof onClose !== 'function') return;
      // Only fire when the click target IS the backdrop element itself;
      // clicks bubbled up from the modal card are ignored.
      if (e.target === e.currentTarget) onClose(e);
    },
  }), [dismissOnBackdrop, onClose]);

  // stopPropagation on the card so clicks inside the modal don't bubble
  // up to the backdrop handler and accidentally close.
  const cardProps = useMemo(() => ({
    onClick: (e) => e.stopPropagation(),
  }), []);

  return { backdropProps, cardProps };
}

/**
 * Hit-target style for icon-only close buttons. Bumps the button to a
 * 44×44 hit area (iOS HIG / WCAG 2.5.5 minimum) while keeping the visual
 * icon size unchanged. Apply with: `style={CLOSE_BTN_STYLE}` and
 * `className="touch-manipulation"` on the <button>.
 */
export const CLOSE_BTN_STYLE = { minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

export default useModalClose;
